import { useState } from 'react';
import { type UseCreateHookResult } from '@saviynt/biz-entity-catalog';

import { createClient } from '../data-sources';
import {
  type Client,
  type ClientFormSubmitPayload,
  type CreateClientResult,
} from '../types';

/**
 * BizEntityCatalog create hook adapter for client credentials.
 */
export function useClientCreate(): UseCreateHookResult<Client> {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const mutate = async (data: Partial<Client>): Promise<Client> => {
    setIsLoading(true);
    setError(null);

    try {
      // Cast to ClientFormSubmitPayload — the form submits this shape through
      // BizEntityCatalog's Partial<Client> boundary (see ClientForm.handleSubmit).
      const formData = data as unknown as ClientFormSubmitPayload;

      const response = await createClient({
        clientName: formData.clientName,
        description: formData.description,
        roles: formData.roles,
        customClaims: formData.customClaims,
        publicCert: formData.publicCert ?? undefined,
        jwksUrl: formData.jwksUrl,
      });

      // Return a Client-shaped object from the create response
      return {
        id: response.clientId,
        clientId: response.clientId,
        clientName: formData.clientName,
        description: formData.description,
        roles: response.roles,
        customClaims: response.customClaims,
        enabled: true,
        certificateCreatedAt: null,
        jwksUrl: formData.jwksUrl ?? null,
        audience: response.audience,
      } as CreateClientResult;
    } catch (err) {
      const typedError = err instanceof Error ? err : new Error(String(err));

      setError(typedError);
      throw typedError;
    } finally {
      setIsLoading(false);
    }
  };

  return { mutate, isLoading, error };
}
